// Two releases side by side (Console D's Releases, APS-307): one row per signal, grouped
// Stability, Performance and Issues, with the change, a delta bar centred on zero (better to the
// left, worse to the right) and why it moved, where the product can tell. The list's comparison
// and a release's "Performance vs previous" are both this table.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Fragment, type ReactNode } from 'react'
import {
  appVitalsQuery,
  compareQuery,
  type DeviceParams,
  endpointsQuery,
  issuesQuery,
  vitalsQuery,
} from '../api/queries'
import type { IssueSummary, ReleaseHealth } from '../api/types'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { duration, percentSpaced, points } from '../lib/format'
import {
  deltaBar,
  releaseFacts,
  SIGNALS,
  type Signal,
  type SignalKey,
  type SignalUnit,
  stateCounts,
} from '../lib/releases'
import { useProjectParams } from '../routes/params'

/** The days every read of the comparison covers (the releases' window). */
const DAYS = 30
/** An endpoint needs this many requests in the candidate to lead the failed row. */
const MIN_REQUESTS = 50
/** Issue links a Why cell shows at most. */
const WHY_LINKS = 2

export interface PairRow {
  signal: Signal
  label: string
  base: number | null
  candidate: number | null
  /** Why it moved: links to what the product knows caused it. */
  why?: ReactNode
}

type Scope = { app?: string }

/**
 * The rows comparing `base` with `candidate` of one app, and the candidate's new issues. Every
 * read is shared with the rest of the page by its query key, so the list and a release's page
 * read each once.
 */
export function useComparison({
  projectId,
  appId,
  android,
  base,
  candidate,
  device,
}: {
  projectId: string
  appId: string | undefined
  /** The bar's device filters, where the page applies them (the list, not a release's page). */
  device?: DeviceParams | undefined
  android: boolean
  base: ReleaseHealth | undefined
  candidate: ReleaseHealth | undefined
}): {
  rows: PairRow[]
  newIssues: IssueSummary[]
  /** The API's change over the same range (D79), when there is a base. */
  change:
    | { crash_free_sessions_rate: number | null; crash_free_users_rate: number | null }
    | undefined
  /** Issues regressed and resolved in the candidate. */
  regressedHere: IssueSummary[]
  resolvedHere: IssueSummary[]
  pending: boolean
} {
  const { t } = useI18n()
  const app = appId ?? ''
  const both = !!appId && !!base && !!candidate
  const vitals = useQuery({ ...appVitalsQuery(projectId, app, device), enabled: !!appId })
  const server = useQuery({
    ...compareQuery(projectId, app, base?.release ?? '', candidate?.release ?? '', device),
    enabled: both,
  })
  const regressedList = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      days: DAYS,
      state: 'regressed',
      limit: 200,
      ...device,
    }),
    enabled: !!appId,
  })
  const resolvedList = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      days: DAYS,
      state: 'resolved',
      limit: 200,
      ...device,
    }),
    enabled: !!appId,
  })
  const vc = useQuery({
    ...vitalsQuery(projectId, app, candidate?.release ?? '', device),
    enabled: !!appId && !!candidate,
  })
  const vb = useQuery({
    ...vitalsQuery(projectId, app, base?.release ?? '', device),
    enabled: both,
  })
  const ec = useQuery({
    ...endpointsQuery(
      projectId,
      { app_id: appId, release: candidate?.release, days: DAYS, ...device },
      'error_rate',
      10,
      MIN_REQUESTS,
    ),
    enabled: !!appId && !!candidate,
  })
  const eb = useQuery({
    ...endpointsQuery(
      projectId,
      { app_id: appId, release: base?.release, days: DAYS, ...device },
      'requests',
      200,
    ),
    enabled: both,
  })

  if (!candidate)
    return {
      rows: [],
      newIssues: [],
      change: undefined,
      regressedHere: [],
      resolvedHere: [],
      pending: true,
    }
  const regressed = regressedList.data
    ? stateCounts(regressedList.data.issues ?? [], 'regressed_release')
    : undefined
  const resolved = resolvedList.data
    ? stateCounts(resolvedList.data.issues ?? [], 'resolved_release')
    : undefined
  const vitalsOf = (r: ReleaseHealth) => vitals.data?.find((v) => v.release === r.release)
  const fc = releaseFacts(candidate, vitalsOf(candidate), android, regressed, resolved)
  const fb = base ? releaseFacts(base, vitalsOf(base), android, regressed, resolved) : undefined

  // The screen row: the candidate's most loaded screen, its full display p90 (initial display
  // when the app never reports full display), on both releases.
  const screen = vc.data?.screens?.[0]
  const screenOf = (rows: typeof vc.data) => rows?.screens?.find((s) => s.screen === screen?.screen)
  const fullDisplay = !!screen?.ttfd
  const p90 = (s: ReturnType<typeof screenOf>) =>
    s ? ((fullDisplay ? s.ttfd?.p90_ms : s.ttid?.p90_ms) ?? null) : null
  fc.screen = p90(screen)
  if (fb) fb.screen = p90(screenOf(vb.data))

  // The failed row: the candidate's endpoint that fails most, against the same one in the base.
  const failing = (ec.data?.endpoints ?? []).find((e) => (e.error_rate ?? 0) > 0)
  const sameEndpoint = failing
    ? eb.data?.endpoints?.find(
        (e) =>
          e.method === failing.method && e.host === failing.host && e.template === failing.template,
      )
    : undefined
  fc.endpoint = failing?.error_rate ?? null
  if (fb) fb.endpoint = sameEndpoint ? (sameEndpoint.error_rate ?? 0) : null

  const newIssues = base ? (server.data?.new_issues ?? []) : []
  const regressedHere = (regressedList.data?.issues ?? []).filter(
    (i) => i.state?.regressed_release === candidate.release,
  )
  const resolvedHere = (resolvedList.data?.issues ?? []).filter(
    (i) => i.state?.resolved_release === candidate.release,
  )
  const crashes = newIssues.filter((i) => i.kind === 'crash')
  const freezes = [...regressedHere, ...newIssues].filter((i) =>
    android ? i.kind === 'anr' : i.kind === 'hang',
  )

  const why: Partial<Record<SignalKey, ReactNode>> = {
    cfs: <IssueLinks issues={crashes} app={appId} />,
    cfu: <IssueLinks issues={crashes} app={appId} />,
    crashedPerK: <IssueLinks issues={crashes} app={appId} />,
    freezePerK: <IssueLinks issues={freezes} app={appId} />,
    newIssues: <IssueLinks issues={newIssues} app={appId} />,
    regressed: <IssueLinks issues={regressedHere} app={appId} />,
    resolved: <IssueLinks issues={resolvedHere} app={appId} />,
    cold90: <StartLink app={appId} release={candidate.release} />,
    cold50: <StartLink app={appId} release={candidate.release} />,
    warm90: <StartLink app={appId} release={candidate.release} />,
    screen: screen ? (
      <ScreenLink screen={screen.screen} app={appId} release={candidate.release} />
    ) : undefined,
    endpoint: failing ? (
      <EndpointLink endpoint={failing} app={appId} release={candidate.release} />
    ) : undefined,
  }

  const label = (s: Signal): string => {
    if (s.key === 'screen')
      return t(fullDisplay ? 'releases.signal.screenFull' : 'releases.signal.screenInitial', {
        screen: screen?.screen ?? '',
      })
    if (s.key === 'endpoint')
      return t('releases.signal.endpoint', {
        endpoint: failing ? `${failing.method} ${failing.template}` : '',
      })
    if (s.key === 'freezePerK')
      return t(android ? 'releases.signal.anrPerK' : 'releases.signal.hangPerK')
    return t(SIGNAL_LABEL[s.key])
  }

  const rows: PairRow[] = SIGNALS.filter((s) => {
    // A row the candidate has nothing for is left out, never shown empty.
    if (s.key === 'screen') return !!screen
    if (s.key === 'endpoint') return !!failing
    if (s.key === 'cfu') return fc.cfu !== null || (fb?.cfu ?? null) !== null
    return fc[s.key] !== null || (fb !== undefined && fb[s.key] !== null)
  }).map((s) => ({
    signal: s,
    label: label(s),
    base: fb ? fb[s.key] : null,
    candidate: fc[s.key],
    why: why[s.key],
  }))

  return {
    rows,
    newIssues,
    change: base ? server.data?.change : undefined,
    regressedHere,
    resolvedHere,
    pending: vitals.isPending || (both && server.isPending),
  }
}

const SIGNAL_LABEL: Record<Exclude<SignalKey, 'screen' | 'endpoint' | 'freezePerK'>, MessageId> = {
  cfs: 'releases.signal.cfs',
  cfu: 'releases.signal.cfu',
  crashedPerK: 'releases.signal.crashedPerK',
  abnormalPerK: 'releases.signal.abnormalPerK',
  cold90: 'releases.signal.cold90',
  cold50: 'releases.signal.cold50',
  warm90: 'releases.signal.warm90',
  newIssues: 'releases.signal.newIssues',
  regressed: 'releases.signal.regressed',
  resolved: 'releases.signal.resolved',
}

const GROUP_LABEL: Record<Signal['group'], MessageId> = {
  stability: 'releases.group.stability',
  performance: 'releases.group.performance',
  issues: 'releases.group.issues',
}

/** A signal's value as the comparison prints it. */
export function signalValue(i18n: Translator, unit: SignalUnit, v: number | null): string {
  if (v === null) return '—'
  switch (unit) {
    case 'rate':
      return percentSpaced(i18n, v, 2)
    case 'failed':
      return percentSpaced(i18n, v, 1)
    case 'perK':
      return i18n.number(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    case 'ms':
      return duration(i18n, v)
    case 'count':
      return i18n.number(v)
  }
}

/** The change from `base` to `candidate`, signed, in the signal's unit. */
function signalChange(i18n: Translator, s: Signal, d: number): string {
  const sign = d < 0 ? '−' : '+'
  switch (s.unit) {
    case 'rate':
      return i18n.t('releases.change.points', { points: points(i18n, d * 100) })
    case 'failed':
      return i18n.t('releases.change.points', {
        points: `${sign}${i18n.number(Math.abs(d) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`,
      })
    case 'perK':
      return `${sign}${i18n.number(Math.abs(d), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    case 'ms':
      return `${sign}${duration(i18n, Math.abs(d))}`
    case 'count':
      return `${sign}${i18n.number(Math.abs(d))}`
  }
}

/** The Change cell: in the ok colour when better, the signal's own when worse. */
export function ChangeCell({
  s,
  base,
  candidate,
}: {
  s: Signal
  base: number | null
  candidate: number | null
}) {
  const i18n = useI18n()
  if (base === null || candidate === null) return <span className="faint">—</span>
  const d = candidate - base
  if (Math.abs(d) <= (s.eps ?? 1e-9))
    return <span className="faint">{i18n.t('releases.change.none')}</span>
  const better = s.better === 'up' ? d > 0 : d < 0
  return (
    <span className={better ? 'c-ok' : s.tone === 'crash' ? 'c-crash' : 'c-warn'}>
      {signalChange(i18n, s, d)}
    </span>
  )
}

/** The delta bar: the relative change, centred on zero; past 200 % it shows a break. */
export function DeltaBarCell({
  s,
  base,
  candidate,
}: {
  s: Signal
  base: number | null
  candidate: number | null
}) {
  const { t } = useI18n()
  const bar = deltaBar(s, base, candidate)
  if (!bar) return <span className="rs-db" title={t('releases.change.none')} />
  const pct = Number.isFinite(bar.relative) ? Math.round(bar.relative * 100) : undefined
  const text = t(bar.worse ? 'releases.delta.worse' : 'releases.delta.better', {
    percent: pct === undefined || bar.over ? '200' : String(pct),
    over: pct === undefined || bar.over ? 'more' : 'exact',
  })
  const cls = bar.worse ? (s.tone === 'crash' ? 'bad' : 'warn') : 'ok'
  return (
    <span className="rs-db" role="img" aria-label={text} title={text}>
      <i
        className={bar.over ? `${cls} over` : cls}
        style={{
          [bar.worse ? 'left' : 'right']: '50%',
          width: `${(bar.width * 50).toFixed(1)}%`,
        }}
      />
    </span>
  )
}

/** The comparison table: Signal, the base, the candidate, Change, the delta bar, and why. */
/**
 * A signal's name with a line break allowed after each slash (an endpoint's path), so a narrow
 * column wraps it between segments, never inside one (APS-583). A <wbr> adds no text.
 */
export function slashBreaks(label: string): ReactNode {
  const parts = label.split(/(?<=\/)/)
  if (parts.length < 2) return label
  return parts.map((part, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: a name's segments keep their order
    <Fragment key={i}>
      {i > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ))
}

export function CompareTable({
  label,
  base,
  candidate,
  rows,
  last,
  groups = true,
  accent = 'cfs',
}: {
  label: string
  base: string
  candidate: string
  rows: PairRow[]
  /** The last column's head: Why on the list, Where on a release. */
  last: string
  groups?: boolean
  /** The row that is the view's one accent, if any. */
  accent?: SignalKey | undefined
}) {
  const i18n = useI18n()
  const { t } = i18n
  let group: Signal['group'] | undefined
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable
    <section className="scroll-x" aria-label={label} tabIndex={0}>
      <table className="t rs-cmp">
        <thead>
          <tr>
            <th className="f" scope="col">
              {t('releases.col.signal')}
            </th>
            <th className="num" scope="col" translate="no">
              {base}
            </th>
            <th className="num" scope="col" translate="no">
              {candidate}
            </th>
            <th className="num" scope="col">
              {t('releases.col.change')}
            </th>
            <th className="rs-dbc" scope="col" title={t('releases.delta.head')}>
              <span className="rs-dbh">{t('releases.delta.worseArrow')}</span>
            </th>
            <th className="last" scope="col">
              {last}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const head =
              groups && r.signal.group !== group ? (
                <tr className="grp">
                  <td className="f" colSpan={6}>
                    {/* The row spans the table: its label is a span so it can stay in view
                        while the table scrolls on a phone, as the signal column does (APS-583). */}
                    <span className="rs-grpl">{t(GROUP_LABEL[r.signal.group])}</span>
                  </td>
                </tr>
              ) : null
            group = r.signal.group
            return (
              <Fragment key={r.signal.key}>
                {head}
                <tr className={r.signal.key === accent ? 'cur' : undefined}>
                  <td
                    className="f"
                    translate={
                      r.signal.key === 'screen' || r.signal.key === 'endpoint' ? 'no' : undefined
                    }
                  >
                    {slashBreaks(r.label)}
                  </td>
                  <td className="num">{signalValue(i18n, r.signal.unit, r.base)}</td>
                  <td className="num">{signalValue(i18n, r.signal.unit, r.candidate)}</td>
                  <td className="num rs-chg">
                    <ChangeCell s={r.signal} base={r.base} candidate={r.candidate} />
                  </td>
                  <td className="rs-dbc">
                    <DeltaBarCell s={r.signal} base={r.base} candidate={r.candidate} />
                  </td>
                  <td className="wrap rs-why last">{r.why ?? <span className="faint">—</span>}</td>
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}

function IssueLinks({ issues, app }: { issues: readonly IssueSummary[]; app: string | undefined }) {
  const { orgId, projectId } = useProjectParams()
  if (issues.length === 0) return null
  return (
    <>
      {issues.slice(0, WHY_LINKS).map((i) => (
        <Link
          key={i.id}
          className="lk"
          to="/o/$orgId/p/$projectId/issues/$issueId"
          params={{ orgId, projectId, issueId: i.id }}
          search={scopeOf(app)}
          translate="no"
          title={i.id}
        >
          {i.id}
        </Link>
      ))}
      {issues.length > WHY_LINKS ? (
        <span className="faint">{`+${issues.length - WHY_LINKS}`}</span>
      ) : null}
    </>
  )
}

function StartLink({ app, release }: { app: string | undefined; release: string }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (
    <Link
      className="lk"
      to="/o/$orgId/p/$projectId/performance/start"
      params={{ orgId, projectId }}
      search={{ ...scopeOf(app), release }}
    >
      {t('releases.why.appStart')}
    </Link>
  )
}

function ScreenLink({
  screen,
  app,
  release,
}: {
  screen: string
  app: string | undefined
  release: string
}) {
  const { orgId, projectId } = useProjectParams()
  return (
    <Link
      className="lk"
      to="/o/$orgId/p/$projectId/performance/screens/detail"
      params={{ orgId, projectId }}
      search={{ ...scopeOf(app), release, screen }}
      translate="no"
      title={screen}
    >
      {screen}
    </Link>
  )
}

function EndpointLink({
  endpoint,
  app,
  release,
}: {
  endpoint: { method: string; host: string; template: string }
  app: string | undefined
  release: string
}) {
  const { orgId, projectId } = useProjectParams()
  return (
    <Link
      className="lk"
      to="/o/$orgId/p/$projectId/performance/network/endpoint"
      params={{ orgId, projectId }}
      search={{
        ...scopeOf(app),
        release,
        method: endpoint.method,
        host: endpoint.host,
        template: endpoint.template,
      }}
      translate="no"
      title={`${endpoint.method} ${endpoint.host}${endpoint.template}`}
    >
      {`${endpoint.method} ${endpoint.template}`}
    </Link>
  )
}

const scopeOf = (app: string | undefined): Scope => (app ? { app } : {})
